import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import { ThemeProvider } from '@/components/ui/theme-provider';
import { AuthCallback } from '@/components/auth/auth-callback.tsx';
import { Branding } from '@/context/branding-context';
import AssetConsoleView from '@/components/features/assets/asset-console-view';
import { AssetsOverviewList } from '@/components/features/assets/assets-overview-list';
import { AssetsPublicView } from './components/features/assets/asset-public-view';
import ServiceManagementPanel from '@/components/features/assets/service-management-panel';
import ServiceDetailView from '@/components/features/services/service-detail-view';
import { Login, Register } from '@/components/auth';
import UserProfile from '@/components/features/user/user-profile';
import AppShell from '@/components/layout/app-shell.tsx';
import { UserProvider, useUser } from '@/context/user-provider';
import { PermissionProvider } from '@/context/permission-provider';
import { OrganizationManagement } from '@/components/features/organizations/organization-management';
import { ApiKeyManagement } from '@/components/features/api-keys/api-key-management';
import AdminDashboard from '@/components/features/admin/admin-dashboard';
import AdminProtectedRoute from '@/components/layout/admin-protected-route';

import LandingPage from './components/features/landing-page/landing-page';
import { DeviceAuthorizationPage } from '@/components/features/auth/device-authorization';
import { DeviceApprovalPage } from '@/components/features/auth/device-approval';
import { DeviceManagementPage } from '@/components/features/auth/device-management';
import UserSessionManagement from '@/components/features/user/user-session-management';
import { WebRTCControllerClient } from '@/components/features/webrtc/webrtc-controller-client';

// Component for handling auth state and routing
function AppContent() {
  const { isAuthenticated, isLoading } = useUser();
  
  // Set document title dynamically based on branding configuration
  useEffect(() => {
    document.title = Branding.appName;
  }, []);

  if (isLoading) {
    // Show a loading indicator while auth state is being determined
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        padding: '2rem'
      }}>
        <h1 style={{ fontSize: '1.5rem', color: '#4b5563' }}>Loading...</h1>
      </div>
    );
  }

  return (
    <Routes>
    {/* Root route - Onboarding page */}
    <Route path="/" element={<LandingPage />} />
    
    {/* Assets route with optional UUID parameter */}
    <Route path="/assets/:assetId" element={<AssetsPublicView />} />
    <Route path="/assets/:assetId/full" element={<AssetsPublicView fullScreen />} />
    
    {/* Auth routes - always accessible */}
    <Route path="/login" element={isAuthenticated ? <Navigate to="/console" /> : <Login />} />
    <Route path="/register" element={isAuthenticated ? <Navigate to="/console" /> : <Register />} />
    <Route path="/api/auth/callback/*" element={<AuthCallback />} />
    
    {/* Device Authorization routes */}
    <Route path="/device" element={<DeviceAuthorizationPage />} />
    <Route path="/device/approve" element={<DeviceApprovalPage />} />
    
    {/* Protected console routes */}
    <Route path="/console/*" element={isAuthenticated ? <AppShell /> : <Navigate to="/login" />}>
      <Route index element={<Navigate to="assets" replace />} />
      <Route path="assets" element={<AssetsOverviewList />} />
      <Route path="assets/:assetId" element={<AssetConsoleView />} />
      <Route path="assets/:assetId/:tab" element={<AssetConsoleView />} />
      <Route path="services" element={<ServiceManagementPanel />} />
      <Route path="services/:serviceId" element={<ServiceDetailView />} />
      <Route path="services/:serviceId/:tab" element={<ServiceDetailView />} />
      <Route path="profile" element={<UserProfile />} />
      <Route path="organizations" element={<OrganizationManagement />} />
      <Route path="organizations/members" element={<OrganizationManagement />} />
      <Route path="organizations/assets" element={<OrganizationManagement />} />
      <Route path="organizations/invitations" element={<OrganizationManagement />} />
      <Route path="api-keys" element={<ApiKeyManagement />} />
      <Route path="devices" element={<DeviceManagementPage />} />
      <Route path="sessions" element={<UserSessionManagement />} />
      <Route path="webrtc-controller" element={<WebRTCControllerClient />} />
      {/* Admin routes - protected by admin role */}
      <Route element={<AdminProtectedRoute />}>
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="admin/users" element={<AdminDashboard />} />
        <Route path="admin/roles" element={<AdminDashboard />} />
        <Route path="admin/sessions" element={<AdminDashboard />} />
      </Route>
    </Route>
    
    {/* Catch-all route for unmatched paths */}
    <Route path="*" element={
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        padding: '2rem'
      }}>
        <h1 style={{ fontSize: '3rem', color: '#6b7280', marginBottom: '1rem' }}>404</h1>
        <h2 style={{ fontSize: '1.5rem', color: '#4b5563', marginBottom: '1rem' }}>Page Not Found</h2>
        <p style={{ color: '#6b7280', marginBottom: '2rem', textAlign: 'center' }}>
          The page you're looking for doesn't exist or has been moved.
        </p>
        <a 
          href="/" 
          style={{ 
            padding: '0.5rem 1rem', 
            backgroundColor: '#3b82f6', 
            color: 'white', 
            textDecoration: 'none', 
            borderRadius: '0.375rem',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
        >
          Go Home
        </a>
      </div>
    } />
  </Routes>
  );
}

// Wrapper component to provide auth context with navigation support
const AppWithAuth = () => {
  const navigate = useNavigate();
  
  const handleLogoutRedirect = (path?: string) => {
    navigate(path || '/login');
  };

  return (
    <UserProvider onLogoutRedirect={handleLogoutRedirect}>
      <PermissionProvider>
        <AppContent />
      </PermissionProvider>
    </UserProvider>
  );
};

function App() {
  const queryClient = new QueryClient();

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppWithAuth />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App
